import React, { useContext, useEffect, useMemo, useState } from "react"
import { useFormContext } from "react-hook-form"
import { useInfiniteQuery } from "react-query"
import { toTaxPercent } from "src/main/Billing/Items/helpers"
import { GeneralSettingsContext } from "src/main/Settings/General"

import Form from "src/components/Form"
import HeadingPageTitle from "src/components/Heading/HeadingPageTitle"
import ReloadableWidget from "src/components/ReloadableWidget"
import TaxRateDisplayInput from "src/components/TaxRateDisplayInput"

import { queryProducts } from "src/api/Billing/Items"

import { snakecaseToTitlecase } from "src/utils/string_helpers"

const SURCHARGE_AVAILABILITIES = ["convenience_fees"]

const Surcharges = () => {
  const [displayTaxRate, setDisplayTaxRate] = useState("")
  const [taxes, setTaxes] = useState([])
  const {
    formState: { errors },
    register,
    setValue,
    watch,
  } = useFormContext()
  const {
    canConfigurePosSurcharge,
    canUpdateSettings,
    marina,
    useNewTaxExperience,
  } = useContext(GeneralSettingsContext)
  const marinaSlug = marina.slug
  const [surchargeContractsEnabled, surchargePosEnabled, surchargeProductId] =
    watch([
      "convenience_fees_contracts_enabled",
      "convenience_fees_point_of_sale_enabled",
      "convenience_fee_product_id",
    ])
  const anySurchargeEnabled = surchargeContractsEnabled || surchargePosEnabled

  // fetch products with "convenience_fees" availability
  const {
    isFetching: isLoadingProducts,
    isError: isErrorProducts,
    data: productsData,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
  } = useInfiniteQuery({
    queryKey: ["products", marinaSlug],
    queryFn: ({ pageParam = 1 }) =>
      queryProducts({
        page: pageParam,
        marinaSlug,
        availability: SURCHARGE_AVAILABILITIES,
      }),
    getNextPageParam: (lastPage, pages) => {
      if (lastPage.length) {
        return pages.length + 1
      }
    },
    refetchOnWindowFocus: false,
  })

  const availableFeeProducts = useMemo(
    () => (productsData?.pages || []).flatMap((page) => page),
    [productsData]
  )

  // categorize products with "convenience_fees" availability for select
  const productByCategory = useMemo(() => {
    return availableFeeProducts.reduce((accum, product) => {
      const category = product.product_category?.display_name || "Uncategorized"
      if (!accum[category]) {
        return {
          ...accum,
          [category]: [product],
        }
      }
      accum[category].push(product)
      return accum
    }, {})
  }, [availableFeeProducts])

  useEffect(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [fetchNextPage, hasNextPage, isFetchingNextPage])

  // clear inputs when no surface is enabled
  useEffect(() => {
    if (!anySurchargeEnabled) {
      setValue("convenience_fee_product_id", "")
      setValue("convenience_fee_percent", "")
      setDisplayTaxRate("")
      setTaxes([])
    }
  }, [setValue, anySurchargeEnabled])

  useEffect(() => {
    if (surchargeProductId && !isLoadingProducts && availableFeeProducts) {
      const feeProduct = availableFeeProducts.find(
        (product) => product.id === surchargeProductId
      )
      if (feeProduct) {
        const taxRate = useNewTaxExperience
          ? feeProduct.combined_tax_rate_display
          : `${toTaxPercent(feeProduct.tax_rate)}%`
        const taxes = useNewTaxExperience ? feeProduct.taxes : []
        setDisplayTaxRate(taxRate)
        setTaxes(taxes)
      }
    }
  }, [
    availableFeeProducts,
    surchargeProductId,
    isLoadingProducts,
    setDisplayTaxRate,
    setTaxes,
    setValue,
    useNewTaxExperience,
  ])

  const handleProductsLinkClick = () => {
    window.open(`/manage/${marinaSlug}/items`, "_blank", "noreferrer")
  }

  return (
    <div className="flex flex-col space-y-4">
      <HeadingPageTitle>Credit Card Surcharges</HeadingPageTitle>
      <span>
        Optionally charge a credit card surcharge to customers. This option
        enables a % fee of up to 3% on the surcharged amount. &quot;Fee&quot;
        type items available for configuration as a surcharge must have
        &quot;surcharge&quot; set on the item&apos;s &quot;availability&quot;
        settings. Configure items and their settings (including tax rate)
        <span
          className="text-link ml-1 font-semibold"
          onClick={handleProductsLinkClick}
        >
          here
        </span>
        .
      </span>
      <div className="flex flex-col gap-4 pb-6">
        <ReloadableWidget
          isLoading={isLoadingProducts || hasNextPage !== false}
          isError={isErrorProducts}
          loadingText="Loading surcharge settings"
        >
          <div className="flex flex-row gap-4">
            <div className="flex w-1/2 flex-col">
              <Form.Label htmlFor="convenience_fee_product_id">
                Billing Item
              </Form.Label>
              <Form.Select
                id="convenience_fee_product_id"
                {...register("convenience_fee_product_id", {
                  required: anySurchargeEnabled
                    ? "A selection is required to utilize the surcharge feature."
                    : false,
                })}
                hasErrors={Boolean(errors?.convenience_fee_product_id)}
                disabled={!canUpdateSettings || !anySurchargeEnabled}
              >
                <option disabled value="">
                  Select billing item ...
                </option>
                {Object.keys(productByCategory).map((category) => {
                  return (
                    <optgroup
                      label={snakecaseToTitlecase(category)}
                      key={category}
                    >
                      {productByCategory[category].map(({ id, name }) => (
                        <option key={id} value={id}>
                          {name}
                        </option>
                      ))}
                    </optgroup>
                  )
                })}
              </Form.Select>
              {errors?.convenience_fee_product_id && (
                <Form.Error>
                  {errors.convenience_fee_product_id.message}
                </Form.Error>
              )}
            </div>
            <div className="flex w-1/4 flex-col">
              <Form.Label htmlFor="percent">Fee Percent</Form.Label>
              <Form.IconTextField
                id="percent"
                position="right"
                icon="%"
                {...register("convenience_fee_percent", {
                  required: anySurchargeEnabled
                    ? "A fee % is required to utilize the surcharge feature."
                    : false,
                  min: {
                    value: 0.001,
                    message: "Fee percent cannot be less than 0.001%",
                  },
                  max: {
                    value: 3.0,
                    message: "Fee percent cannot exceed 3%.",
                  },
                  maxLength: {
                    value: 5,
                    message:
                      "Fee percent cannot have more than three decimal places.",
                  },
                })}
                type="number"
                inputMode="decimal"
                hasErrors={Boolean(errors?.convenience_fee_percent)}
                disabled={!canUpdateSettings || !anySurchargeEnabled}
              />
              {errors?.convenience_fee_percent && (
                <Form.Error>
                  {errors.convenience_fee_percent.message}
                </Form.Error>
              )}
            </div>
            <div className="flex w-1/4 flex-col">
              <TaxRateDisplayInput
                combinedTaxRateDisplay={displayTaxRate}
                taxes={taxes}
                label="Tax"
              />
            </div>
          </div>
          <Form.Checkbox
            {...register("convenience_fees_contracts_enabled")}
            label="Enable for contract invoices"
            disabled={!canUpdateSettings}
          />
          {canConfigurePosSurcharge && (
            <Form.Checkbox
              {...register("convenience_fees_point_of_sale_enabled")}
              label="Enable for POS & fuel"
              disabled={!canUpdateSettings}
            />
          )}
        </ReloadableWidget>
      </div>
    </div>
  )
}

export default Surcharges
